import { AppError } from "@shared/app-error";

/**
 * §3.3 / §5.2「スタイル配布と PiP Window」: メイン Window で生成された CSS を
 * PiP Window と Constructable StyleSheets + adoptedStyleSheets 経由で共有する。
 *
 * 採用しない方式:
 * - `<link rel="stylesheet">` の cloneNode: 別 Window で再フェッチが走って FOUC が出る
 *   (320×200 の小領域で誤検知ボタン位置がズレる)
 *
 * 採用方式:
 * - メイン Window の `document.styleSheets` を巡回し、 同一 origin の cssRules を
 *   テキストとして抽出 → CSSStyleSheet を構築して module スコープに 1 個だけキャッシュ
 * - PiP Window の `document.adoptedStyleSheets` に同じ参照を push する
 *
 * `url(...)` 参照は配置先 Window の base URL に依存しないよう絶対パスを推奨 (§5.2)。
 * Vite 8 の本番ビルドはハッシュ付きの絶対パスを生成するため通常は問題ない。
 */

// CSS テキストは構築コストが高い (全 stylesheet を巡回) ので一度だけ抽出してキャッシュ。
let sharedCssText: string | null = null;
// CSSStyleSheet は constructor document に紐付く (= 生成時の Realm 専用)。 main で new した
// sheet を PiP document に adopt すると NotAllowedError になるため、 document 単位で生成し
// 別 PiP セッションでも再利用できるよう WeakMap でキャッシュする。
const sheetByDocument = new WeakMap<Document, CSSStyleSheet>();

function extractCssText(): string {
  if (sharedCssText !== null) return sharedCssText;
  let cssText = "";
  for (const stylesheet of Array.from(document.styleSheets)) {
    try {
      for (const rule of Array.from(stylesheet.cssRules)) {
        cssText += `${rule.cssText}\n`;
      }
    } catch {
      // cross-origin の link はアクセス禁止 (SecurityError)。 fonts.googleapis 等は
      // PiP Window 側でも独立に <link> を張るのではなく、 ここでは諦めて Tailwind / 自前
      // CSS だけ共有する。 フォントは PiP の小領域では default fallback でも実害なし。
    }
  }
  sharedCssText = cssText;
  return cssText;
}

export function attachSharedStyleSheetTo(targetDocument: Document): void {
  const cached = sheetByDocument.get(targetDocument);
  if (cached) {
    if (!targetDocument.adoptedStyleSheets.includes(cached)) {
      targetDocument.adoptedStyleSheets = [...targetDocument.adoptedStyleSheets, cached];
    }
    return;
  }
  // targetDocument の Realm にある CSSStyleSheet コンストラクタで生成する。
  // (main 側の `new CSSStyleSheet()` を別 document に adopt すると NotAllowedError)
  const win = targetDocument.defaultView;
  if (!win) {
    throw new AppError(
      "PipStylesheetNoRealm",
      "transient",
      "targetDocument has no defaultView; cannot build a same-realm CSSStyleSheet",
    );
  }
  const SheetCtor = win.CSSStyleSheet;
  const sheet = new SheetCtor();
  sheet.replaceSync(extractCssText());
  sheetByDocument.set(targetDocument, sheet);
  targetDocument.adoptedStyleSheets = [...targetDocument.adoptedStyleSheets, sheet];
}
